import type { Book, Category } from '@/types/catalog';

// Server-side data layer. Called from server components; talks straight to the
// fce-books-api with the required User-Agent. Client mutations (auth, favorites,
// borrow) go through the /api/backend proxy instead.
const API_BASE = process.env.NEXT_PUBLIC_API_URI || 'http://localhost:3010';
const USER_AGENT = process.env.FCE_API_USER_AGENT || 'fce_api-biblioteca-fce_v1';

async function apiGet<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'User-Agent': USER_AGENT },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export interface ApiBook {
  id: string;
  title: string;
  subtitle?: string | null;
  author: string;
  year?: number | null;
  pages?: number | null;
  isbn?: string | null;
  subject?: string | null;
  category_slug?: string | null;
  category_name?: string | null;
  cover_image_url?: string | null;
  summary?: string | null;
  full_summary?: string | null;
  language?: string | null;
  product?: string | null;
  duration_ms?: number | null;
}

export function toBook(r: ApiBook): Book {
  return {
    id: r.id,
    title: r.title,
    author: r.author,
    subtitle: r.subtitle ?? undefined,
    year: r.year ?? undefined,
    pages: r.pages ?? undefined,
    isbn: r.isbn ?? undefined,
    subject: r.subject ?? undefined,
    categorySlug: r.category_slug ?? undefined,
    categoryName: r.category_name ?? undefined,
    coverUrl: r.cover_image_url ?? undefined,
    summary: r.summary ?? undefined,
    fullSummary: r.full_summary ?? undefined,
    language: r.language ?? undefined,
    product: r.product === 'audiobook' ? 'audiobook' : 'ebook',
    durationMs: r.duration_ms ?? undefined,
  };
}

export type Shelves = { novedades: Book[]; masLeidos: Book[]; recomienda: Book[]; ninos: Book[] };

export async function getHomeShelves(): Promise<Shelves> {
  const d = await apiGet<Record<keyof Shelves, ApiBook[]>>('/home/shelves');
  const pick = (k: keyof Shelves) => (d?.[k] ?? []).map(toBook);
  return { novedades: pick('novedades'), masLeidos: pick('masLeidos'), recomienda: pick('recomienda'), ninos: pick('ninos') };
}

export async function getCategories(): Promise<Category[]> {
  const d = await apiGet<{ items: Category[] }>('/categories');
  return d?.items ?? [];
}

export interface BookQuery {
  category?: string;
  author?: string;
  subject?: string;
  year?: number | string;
  sort?: string;
  order?: string;
  limit?: number;
  offset?: number;
}

export async function listBooks(q: BookQuery = {}): Promise<{ items: Book[]; total: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const d = await apiGet<{ items: ApiBook[]; total: number }>(`/books?${params.toString()}`);
  return { items: (d?.items ?? []).map(toBook), total: d?.total ?? 0 };
}

export async function getBook(id: string): Promise<Book | null> {
  const d = await apiGet<ApiBook>(`/books/${encodeURIComponent(id)}`);
  return d ? toBook(d) : null;
}

export interface Facets {
  authors: string[];
  years: number[];
  subjects: string[];
}

export async function getFacets(category?: string): Promise<Facets> {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  const d = await apiGet<Facets>(`/books/facets${qs}`);
  return d ?? { authors: [], years: [], subjects: [] };
}

export async function searchBooks(q: string): Promise<Book[]> {
  if (!q.trim()) return [];
  const d = await apiGet<{ items: ApiBook[] }>(`/search?q=${encodeURIComponent(q)}`);
  return (d?.items ?? []).map(toBook);
}

export async function getBooksByAuthor(author: string): Promise<Book[]> {
  return (await listBooks({ author, limit: 60 })).items;
}
